import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface ChangeLog {
  id: string
  entity_type: string
  entity_id: string
  entity_name: string
  action: string
  field_changes: { field: string; old_value: string | null; new_value: string }[]
  changed_by: string
  changed_at: string
  reason: string
  version: number
  rollback_available: boolean
}

interface Snapshot {
  id: string
  name: string
  description: string
  created_by: string
  created_at: string
  size_mb: number
  entities_count: number
  status: string
  retention_days: number
}

export default function VersionControlPage() {
  const [changeLogs, setChangeLogs] = useState<ChangeLog[]>([])
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [changesRes, snapshotsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/version-control/changes'),
      fetch(API_BASE + '/version-control/snapshots'),
      fetch(API_BASE + '/version-control/statistics')
    ])
    if (changesRes.ok) setChangeLogs((await changesRes.json()).change_logs || [])
    if (snapshotsRes.ok) setSnapshots((await snapshotsRes.json()).snapshots || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create': return 'bg-green-100 text-green-800'
      case 'update': return 'bg-blue-100 text-blue-800'
      case 'delete': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Version Control & Change Tracking</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Snapshot</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Changes</div>
              <div className="text-2xl font-bold">{statistics.total_changes_logged}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">This Week</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.changes_this_week}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Snapshots</div>
              <div className="text-2xl font-bold">{statistics.total_snapshots}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Rollbacks Available</div>
              <div className="text-2xl font-bold text-green-600">{statistics.rollback_available}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Storage</div>
              <div className="text-2xl font-bold">{statistics.total_storage_mb?.toFixed(1)} MB</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Changes</h2></div>
            <div className="divide-y">
              {changeLogs.map(log => (
                <div key={log.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{log.entity_name}</div>
                      <div className="text-sm text-gray-500">{log.entity_type} • v{log.version}</div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getActionColor(log.action)}`}>{log.action}</span>
                  </div>
                  <div className="space-y-1 mb-2">
                    {log.field_changes.map((fc, i) => (
                      <div key={i} className="text-sm">
                        <span className="text-gray-500">{fc.field}:</span>{' '}
                        <span className="text-red-500 line-through">{fc.old_value || 'null'}</span>{' → '}
                        <span className="text-green-600">{fc.new_value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">{log.changed_by} • {new Date(log.changed_at).toLocaleString()}</span>
                    {log.rollback_available && (
                      <button className="px-3 py-1 bg-orange-100 text-orange-800 rounded hover:bg-orange-200">Rollback</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Snapshots</h3>
              <div className="space-y-3">
                {snapshots.map(snapshot => (
                  <div key={snapshot.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm">{snapshot.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${snapshot.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {snapshot.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{snapshot.description}</p>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{snapshot.entities_count} entities • {snapshot.size_mb.toFixed(1)} MB</span>
                      <span>{new Date(snapshot.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
